/**
 * API Client — Axios-Instanz mit OIDC Bearer-Token-Interceptor (Authentik).
 *
 * Token-Provider wird vom AuthProvider registriert (setTokenProvider).
 * Bei 401: signIn-Trigger (registriert vom AuthProvider) statt hartem Redirect.
 *
 * Terminal-Pfad /api/v1/bde/terminal/* nutzt eine separate Instanz mit
 * X-Device-Token Header (KEIN OIDC-Bearer). Siehe terminalApiClient.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import type {
  PaginatedResponse,
  // CRM Lookups (LEH-098)
  AddressTypeRead,
  AddressTypeCreate,
  AddressTypeUpdate,
  ContactRoleRead,
  ContactRoleCreate,
  ContactRoleUpdate,
  AddressCreate,
  AddressUpdate,
  AddressRead,
  OrganizationContactUpdate,
  // Erw. Fertigung (LEH-094)
  ToolListItem,
  ToolRead,
  ToolCreate,
  ToolUpdate,
  NcProgramListItem,
  NcProgramRead,
  NcProgramCreate,
  NcProgramUpdate,
  InspectionEquipmentListItem,
  InspectionEquipmentRead,
  InspectionEquipmentCreate,
  InspectionEquipmentUpdate,
  CalibrationStatus,
  MaintenancePlanListItem,
  MaintenancePlanRead,
  MaintenancePlanCreate,
  MaintenancePlanUpdate,
  DuePlanItem,
  MaintenanceEntryListItem,
  MaintenanceEntryRead,
  MaintenanceEntryCreate,
  MaintenanceEntryUpdate,
  MachineMaintenanceSummary,
  DefectCatalogRead,
  DefectCatalogCreate,
  DefectCatalogUpdate,
  BdeTerminalRead,
  BdeTerminalWithToken,
  BdeTerminalCreate,
  BdeTerminalUpdate,
  DocumentListItem,
  DocumentRead,
  DocumentUpdate,
  ConnectivityResponse,
  // Beschaffung (LEH-093)
  RfqListItem,
  RfqRead,
  RfqCreate,
  RfqUpdate,
  RfqLineCreate,
  RfqLineUpdate,
  RfqLineRead,
  RfqToQuoteRequest,
  SupplierQuoteListItem,
  SupplierQuoteRead,
  SupplierQuoteCreate,
  SupplierQuoteUpdate,
  QuoteLineCreate,
  QuoteLineUpdate,
  QuoteLineRead,
  QuoteToPoRequest,
  PurchaseOrderListItem,
  PurchaseOrderRead,
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
  PoLineCreate,
  PoLineUpdate,
  PoLineRead,
  QuantityOpenResponse,
  PoToGrRequest,
  GoodsReceiptListItem,
  GoodsReceiptRead,
  GoodsReceiptCreate,
  GoodsReceiptUpdate,
  GrLineCreate,
  GrLineUpdate,
  GrLineRead,
  WarehouseListItem,
  WarehouseRead,
  WarehouseCreate,
  WarehouseUpdate,
  StockLevelRead,
  StockMovementRead,
  PostToStockRequest,
  SupplierPriceListItem,
  SupplierPriceRead,
  SupplierPriceCreate,
  SupplierPriceUpdate,
  SupplierPriceTierCreate,
  SupplierPriceTierUpdate,
  SupplierPriceTierRead,
  PurchaseInvoiceListItem,
  PurchaseInvoiceRead,
  PurchaseInvoiceCreate,
  PurchaseInvoiceUpdate,
  PurchaseInvoiceLineCreate,
  PurchaseInvoiceLineUpdate,
  PurchaseInvoiceLineRead,
  MatchRequest,
  MatchResult,
  OrganizationListItem,
  OrganizationRead,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationContactRead,
  OrganizationContactCreate,
  ContactListItem,
  ContactRead,
  ContactCreate,
  ContactUpdate,
  ArticleListItem,
  ArticleRead,
  ArticleCreate,
  ArticleUpdate,
  ProductionOrderListItem,
  ProductionOrderRead,
  ProductionOrderCreate,
  ProductionOrderUpdate,
  PositionListItem,
  PositionRead,
  PositionCreate,
  PositionUpdate,
  PositionMoveRequest,
  MoveItemRequest,
  CalculationItemRead,
  CalculationItemCreate,
  CalculationItemUpdate,
  WorkStepRead,
  WorkStepCreate,
  WorkStepUpdate,
  OrderCalculationResult,
  WorkPlanRead,
  WorkPlanGenerateResponse,
  BomLineRead,
  BomLineCreate,
  BomLineUpdate,
  CalculationTemplateListItem,
  ApplyTemplateResponse,
  BdeRfidLoginResponse,
  BdeCurrentBookingResponse,
  BdeStartRequest,
  BdeStartResponse,
  BdeStopRequest,
  BdeStopResponse,
  BdePauseRequest,
  BdePauseResponse,
  OrdersOverview,
  PlanVsActual,
  MachineUtilizationRow,
  ProcurementOverview,
  StockOverview,
  RevenueOverview,
  // Stammdaten Fertigung (LEH-090)
  MachineListItem,
  MachineRead,
  MachineCreate,
  MachineUpdate,
  CostCenterGroupListItem,
  CostCenterGroupRead,
  CostCenterGroupCreate,
  CostCenterGroupUpdate,
  CostCenterListItem,
  CostCenterRead,
  CostCenterCreate,
  CostCenterUpdate,
  OperationTypeListItem,
  OperationTypeRead,
  OperationTypeCreate,
  OperationTypeUpdate,
  HourlyRateListItem,
  HourlyRateRead,
  HourlyRateCreate,
  HourlyRateUpdate,
  HourlyRateActivateResponse,
  CostObjectListItem,
  CostObjectRead,
  CostObjectCreate,
  CostObjectUpdate,
  // HR — Personal (LEH-092)
  HrEmployeeListItem,
  HrEmployeeRead,
  HrEmployeeCreate,
  HrEmployeeUpdate,
  HrEmployeeContractListItem,
  HrEmployeeContractRead,
  HrEmployeeContractCreate,
  HrEmployeeContractUpdate,
  HrWorkTimeModelListItem,
  HrWorkTimeModelRead,
  HrWorkTimeModelCreate,
  HrWorkTimeModelUpdate,
  HrLeaveAccountRead,
  HrLeaveRequestRead,
  HrLeaveRequestCreate,
  HrLeaveRequestApprove,
  HrLeaveRequestReject,
  HrLeaveRequestWithWarnings,
  HrCompanyCalendarRead,
  HrHolidayRead,
  HrCalendarGenerateRequest,
} from "@/types/index"
import { OIDC_CONFIGURED } from "./runtime-env"

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1"

// Token Provider fuer die OIDC-Integration (registriert vom AuthProvider)
let _tokenProvider: (() => Promise<string | undefined>) | null = null

export function setTokenProvider(fn: () => Promise<string | undefined>) {
  _tokenProvider = fn
}

export function getTokenProvider() {
  return _tokenProvider
}

// Sign-in trigger (lazy, kein zirkulaerer Import) — wird vom AuthProvider gesetzt
let _signInTrigger: (() => void) | null = null

export function setSignInTrigger(fn: () => void) {
  _signInTrigger = fn
}

// Auth Store lazy importieren (zirkulaere Dep vermeiden)
const getAuthStore = () => import("@/store/authStore").then((m) => m.useAuthStore)

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    })

    // Request interceptor: OIDC Bearer-Token + Tenant-ID
    this.client.interceptors.request.use(async (config) => {
      try {
        // Lokale Sichtpruefung ohne Authentik (04.08.2026): Nur im
        // Vite-Dev-Server UND nur wenn kein OIDC konfiguriert ist. Im
        // gebauten Image ist import.meta.env.DEV false — der Header kann
        // also gar nicht in Staging oder Produktion landen. Das Backend
        // beachtet ihn ohnehin nur bei AUTH_MODE=dev.
        if (import.meta.env.DEV && !OIDC_CONFIGURED) {
          config.headers["X-Dev-User"] = import.meta.env.VITE_DEV_USER || "dev@local"
          config.headers["X-Dev-Roles"] = import.meta.env.VITE_DEV_ROLES || "admin"
        }

        if (_tokenProvider) {
          const token = await _tokenProvider()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }

        const store = await getAuthStore()
        const tenantId = store.getState().tenantId
        if (tenantId) {
          config.headers["X-Tenant-Id"] = tenantId
        }
      } catch {
        // Ignore errors during SSR or before store is ready
      }
      return config
    })

    // Response interceptor — bei 401 signIn() auslösen (Token abgelaufen/fehlt)
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 && _signInTrigger) {
          // Nur auslösen wenn kein OIDC-Callback im Gange (um Login-Loop zu vermeiden)
          const isCallbackRoute = window.location.pathname === "/callback"
          if (!isCallbackRoute) {
            _signInTrigger()
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Generic request method
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { detail?: unknown; message?: unknown } | undefined
        const detail = data?.detail
        const message =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail
                  .map((item: unknown) => {
                    if (typeof item === "object" && item !== null) {
                      const i = item as Record<string, unknown>
                      return typeof i.msg === "string" ? i.msg : JSON.stringify(item)
                    }
                    return String(item)
                  })
                  .join("; ")
              : typeof data?.message === "string"
                ? data.message
                : error.message
        const apiError = new Error(message) as Error & {
          response?: typeof error.response
          status?: number
        }
        apiError.response = error.response
        apiError.status = error.response?.status
        throw apiError
      }
      throw error
    }
  }

  // System
  system = {
    health: async (): Promise<{ status: string; db: string }> => {
      return this.request({
        method: "GET",
        url: "/system/health",
      })
    },
  }

  // Auth
  auth = {
    getMe: async (
      hints?: { email?: string | null; name?: string | null },
    ): Promise<import("@/store/authStore").User> => {
      const headers: Record<string, string> = {}
      // Display-only profile hints from the OIDC ID token (backend keys identity
      // on the verified JWT sub, never on these).
      if (hints?.email) headers["X-Profile-Email"] = hints.email
      if (hints?.name) headers["X-Profile-Name"] = hints.name
      return this.request({
        method: "GET",
        url: "/auth/me",
        headers,
      })
    },
  }

  // ===========================================================================
  // CRM — Organizations (LEH-091)
  // ===========================================================================

  /**
   * Zaehler fuer die Navigationsleiste (nefadesign Phase 4).
   * Ein Aufruf, eine Abfrage — nicht 22 einzelne.
   */
  navCounts = {
    get: async (): Promise<Record<string, number>> => {
      return this.request({ method: "GET", url: "/nav-counts" })
    },
  }

  // Oberflächen-Einstellungen des angemeldeten Benutzers. Kein Benutzer in
  // der Adresse — der Endpunkt kennt nur den Aufrufer.
  userSettings = {
    list: async (): Promise<Record<string, unknown>> => {
      return this.request({ method: "GET", url: "/user-settings" })
    },

    set: async (key: string, value: unknown): Promise<Record<string, unknown>> => {
      return this.request({ method: "PUT", url: "/user-settings", data: { key, value } })
    },
  }

  organizations = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isCustomer?: boolean
      isSupplier?: boolean
      isActive?: boolean
    }): Promise<PaginatedResponse<OrganizationListItem>> => {
      return this.request({
        method: "GET",
        url: "/organizations",
        params,
      })
    },

    get: async (orgId: string): Promise<OrganizationRead> => {
      return this.request({ method: "GET", url: `/organizations/${orgId}` })
    },

    create: async (body: OrganizationCreate): Promise<OrganizationRead> => {
      return this.request({ method: "POST", url: "/organizations", data: body })
    },

    update: async (orgId: string, body: OrganizationUpdate): Promise<OrganizationRead> => {
      return this.request({ method: "PUT", url: `/organizations/${orgId}`, data: body })
    },

    delete: async (orgId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/organizations/${orgId}` })
    },

    addContact: async (orgId: string, body: OrganizationContactCreate): Promise<OrganizationContactRead> => {
      return this.request({ method: "POST", url: `/organizations/${orgId}/contacts`, data: body })
    },

    updateContactLink: async (orgId: string, linkId: string, body: OrganizationContactUpdate): Promise<OrganizationContactRead> => {
      return this.request({ method: "PUT", url: `/organizations/${orgId}/contacts/${linkId}`, data: body })
    },

    addAddress: async (orgId: string, body: AddressCreate): Promise<AddressRead> => {
      return this.request({ method: "POST", url: `/organizations/${orgId}/addresses`, data: body })
    },

    updateAddress: async (orgId: string, addressId: string, body: AddressUpdate): Promise<AddressRead> => {
      return this.request({ method: "PUT", url: `/organizations/${orgId}/addresses/${addressId}`, data: body })
    },

    deleteAddress: async (orgId: string, addressId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/organizations/${orgId}/addresses/${addressId}` })
    },

    removeContact: async (orgId: string, linkId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/organizations/${orgId}/contacts/${linkId}` })
    },
  }

  // ===========================================================================
  // CRM — Address Types (LEH-098)
  // ===========================================================================

  addressTypes = {
    list: async (params?: { activeOnly?: boolean }): Promise<AddressTypeRead[]> => {
      return this.request({ method: "GET", url: "/address-types", params })
    },

    get: async (id: string): Promise<AddressTypeRead> => {
      return this.request({ method: "GET", url: `/address-types/${id}` })
    },

    create: async (body: AddressTypeCreate): Promise<AddressTypeRead> => {
      return this.request({ method: "POST", url: "/address-types", data: body })
    },

    update: async (id: string, body: AddressTypeUpdate): Promise<AddressTypeRead> => {
      return this.request({ method: "PUT", url: `/address-types/${id}`, data: body })
    },

    delete: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/address-types/${id}` })
    },
  }

  // ===========================================================================
  // CRM — Contact Roles (LEH-098)
  // ===========================================================================

  contactRoles = {
    list: async (params?: { activeOnly?: boolean }): Promise<ContactRoleRead[]> => {
      return this.request({ method: "GET", url: "/contact-roles", params })
    },

    get: async (id: string): Promise<ContactRoleRead> => {
      return this.request({ method: "GET", url: `/contact-roles/${id}` })
    },

    create: async (body: ContactRoleCreate): Promise<ContactRoleRead> => {
      return this.request({ method: "POST", url: "/contact-roles", data: body })
    },

    update: async (id: string, body: ContactRoleUpdate): Promise<ContactRoleRead> => {
      return this.request({ method: "PUT", url: `/contact-roles/${id}`, data: body })
    },

    delete: async (id: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/contact-roles/${id}` })
    },
  }

  // ===========================================================================
  // CRM — Contacts (LEH-091)
  // ===========================================================================

  contacts = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      organizationId?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<ContactListItem>> => {
      return this.request({ method: "GET", url: "/contacts", params })
    },

    get: async (contactId: string): Promise<ContactRead> => {
      return this.request({ method: "GET", url: `/contacts/${contactId}` })
    },

    create: async (body: ContactCreate): Promise<ContactRead> => {
      return this.request({ method: "POST", url: "/contacts", data: body })
    },

    update: async (contactId: string, body: ContactUpdate): Promise<ContactRead> => {
      return this.request({ method: "PUT", url: `/contacts/${contactId}`, data: body })
    },

    delete: async (contactId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/contacts/${contactId}` })
    },

    unlinkFromOrganization: async (contactId: string, orgId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/contacts/${contactId}/organizations/${orgId}` })
    },
  }

  // ===========================================================================
  // Artikelstamm — Articles (LEH-091)
  // ===========================================================================

  articles = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      articleType?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<ArticleListItem>> => {
      return this.request({ method: "GET", url: "/articles", params })
    },

    get: async (articleId: string): Promise<ArticleRead> => {
      return this.request({ method: "GET", url: `/articles/${articleId}` })
    },

    create: async (body: ArticleCreate): Promise<ArticleRead> => {
      return this.request({ method: "POST", url: "/articles", data: body })
    },

    update: async (articleId: string, body: ArticleUpdate): Promise<ArticleRead> => {
      return this.request({ method: "PUT", url: `/articles/${articleId}`, data: body })
    },

    delete: async (articleId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/articles/${articleId}` })
    },
  }

  // ===========================================================================
  // Production Orders (FAUF)
  // ===========================================================================

  productionOrders = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      statusProduction?: number
      statusAdmin?: number
      customerId?: string
    }): Promise<PaginatedResponse<ProductionOrderListItem>> => {
      return this.request({
        method: "GET",
        url: "/production-orders",
        params,
      })
    },

    get: async (orderId: string): Promise<ProductionOrderRead> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}`,
      })
    },

    create: async (body: ProductionOrderCreate): Promise<ProductionOrderRead> => {
      return this.request({
        method: "POST",
        url: "/production-orders",
        data: body,
      })
    },

    update: async (orderId: string, body: ProductionOrderUpdate): Promise<ProductionOrderRead> => {
      return this.request({
        method: "PUT",
        url: `/production-orders/${orderId}`,
        data: body,
      })
    },

    delete: async (orderId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/production-orders/${orderId}`,
      })
    },

    // Position tree
    getTree: async (orderId: string): Promise<PositionListItem[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/tree`,
      })
    },

    // Flat position list (all positions for a FAUF, used for move target picker — LEH-102)
    listPositions: async (orderId: string): Promise<PositionListItem[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/positions`,
      })
    },

    addPosition: async (orderId: string, body: PositionCreate): Promise<PositionRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/positions`,
        data: body,
      })
    },

    getPosition: async (orderId: string, posId: string): Promise<PositionRead> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/positions/${posId}`,
      })
    },

    updatePosition: async (orderId: string, posId: string, body: PositionUpdate): Promise<PositionRead> => {
      return this.request({
        method: "PUT",
        url: `/production-orders/${orderId}/positions/${posId}`,
        data: body,
      })
    },

    movePosition: async (orderId: string, posId: string, body: PositionMoveRequest): Promise<PositionRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/positions/${posId}/move`,
        data: body,
      })
    },

    deletePosition: async (orderId: string, posId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/production-orders/${orderId}/positions/${posId}`,
      })
    },

    // Calculation items on order header
    listCalculationItems: async (orderId: string): Promise<CalculationItemRead[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/calculation-items`,
      })
    },

    createCalculationItem: async (orderId: string, body: CalculationItemCreate): Promise<CalculationItemRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/calculation-items`,
        data: body,
      })
    },

    // Calculation items on position
    listPositionCalculationItems: async (orderId: string, posId: string): Promise<CalculationItemRead[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/positions/${posId}/calculation-items`,
      })
    },

    createPositionCalculationItem: async (orderId: string, posId: string, body: CalculationItemCreate): Promise<CalculationItemRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/positions/${posId}/calculation-items`,
        data: body,
      })
    },

    // Work steps on order header
    listWorkSteps: async (orderId: string): Promise<WorkStepRead[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/work-steps`,
      })
    },

    createWorkStep: async (orderId: string, body: WorkStepCreate): Promise<WorkStepRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/work-steps`,
        data: body,
      })
    },

    // Work steps on position
    listPositionWorkSteps: async (orderId: string, posId: string): Promise<WorkStepRead[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/positions/${posId}/work-steps`,
      })
    },

    createPositionWorkStep: async (orderId: string, posId: string, body: WorkStepCreate): Promise<WorkStepRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/positions/${posId}/work-steps`,
        data: body,
      })
    },

    // Live calculation rollup
    getCalculation: async (orderId: string): Promise<OrderCalculationResult> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/calculation`,
      })
    },

    // Work plan
    getWorkPlan: async (orderId: string): Promise<WorkPlanRead> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/work-plan`,
      })
    },

    generateWorkPlan: async (orderId: string): Promise<WorkPlanGenerateResponse> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/work-plan/generate`,
      })
    },

    syncWorkInstructions: async (orderId: string): Promise<WorkPlanGenerateResponse> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/work-plan/sync-instructions`,
      })
    },

    // BOM lines on order header
    listBom: async (orderId: string): Promise<BomLineRead[]> => {
      return this.request({
        method: "GET",
        url: `/production-orders/${orderId}/bom`,
      })
    },

    createBomLine: async (orderId: string, body: BomLineCreate): Promise<BomLineRead> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/bom`,
        data: body,
      })
    },
  }

  // ===========================================================================
  // BOM lines — position-level CRUD
  // ===========================================================================

  bom = {
    listForPosition: async (posId: string): Promise<BomLineRead[]> => {
      return this.request({
        method: "GET",
        url: `/positions/${posId}/bom`,
      })
    },

    createForPosition: async (posId: string, body: BomLineCreate): Promise<BomLineRead> => {
      return this.request({
        method: "POST",
        url: `/positions/${posId}/bom`,
        data: body,
      })
    },

    get: async (lineId: string): Promise<BomLineRead> => {
      return this.request({
        method: "GET",
        url: `/bom-lines/${lineId}`,
      })
    },

    update: async (lineId: string, body: BomLineUpdate): Promise<BomLineRead> => {
      return this.request({
        method: "PUT",
        url: `/bom-lines/${lineId}`,
        data: body,
      })
    },

    delete: async (lineId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/bom-lines/${lineId}`,
      })
    },

    /** PUT /bom-lines/reorder — reassigns sort_order in 10-steps. */
    reorder: async (itemIds: string[]): Promise<BomLineRead[]> => {
      return this.request({
        method: "PUT",
        url: "/bom-lines/reorder",
        data: { itemIds },
      })
    },

    /** PUT /bom-lines/{lineId}/move — move to a different position (LEH-102).
     *  targetPositionId=null moves to FAUF header (Allgemein). */
    move: async (lineId: string, body: MoveItemRequest): Promise<BomLineRead> => {
      return this.request({
        method: "PUT",
        url: `/bom-lines/${lineId}/move`,
        data: body,
      })
    },
  }

  // ===========================================================================
  // Calculation Templates (LEH-025)
  // ===========================================================================

  calculationTemplates = {
    list: async (activeOnly?: boolean): Promise<CalculationTemplateListItem[]> => {
      return this.request({
        method: "GET",
        url: "/calculation-templates",
        params: activeOnly ? { activeOnly: true } : undefined,
      })
    },

    applyToPosition: async (
      orderId: string,
      posId: string,
      tmplId: string
    ): Promise<ApplyTemplateResponse> => {
      return this.request({
        method: "POST",
        url: `/production-orders/${orderId}/positions/${posId}/apply-template/${tmplId}`,
      })
    },
  }

  // ===========================================================================
  // CalculationItem standalone CRUD
  // ===========================================================================

  calculationItems = {
    get: async (itemId: string): Promise<CalculationItemRead> => {
      return this.request({
        method: "GET",
        url: `/calculation-items/${itemId}`,
      })
    },

    update: async (itemId: string, body: CalculationItemUpdate): Promise<CalculationItemRead> => {
      return this.request({
        method: "PUT",
        url: `/calculation-items/${itemId}`,
        data: body,
      })
    },

    delete: async (itemId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/calculation-items/${itemId}`,
      })
    },

    /** PUT /calculation-items/reorder — reassigns calc_pos_no in 10-steps.
     *  Items with calcPosNo==0 must NOT be included in itemIds. */
    reorder: async (itemIds: string[]): Promise<CalculationItemRead[]> => {
      return this.request({
        method: "PUT",
        url: "/calculation-items/reorder",
        data: { itemIds },
      })
    },

    /** PUT /calculation-items/{itemId}/move — move to a different position (LEH-102).
     *  targetPositionId=null moves to FAUF header (Allgemein). */
    move: async (itemId: string, body: MoveItemRequest): Promise<CalculationItemRead> => {
      return this.request({
        method: "PUT",
        url: `/calculation-items/${itemId}/move`,
        data: body,
      })
    },
  }

  // ===========================================================================
  // WorkStep standalone CRUD
  // ===========================================================================

  workSteps = {
    get: async (stepId: string): Promise<WorkStepRead> => {
      return this.request({
        method: "GET",
        url: `/work-steps/${stepId}`,
      })
    },

    update: async (stepId: string, body: WorkStepUpdate): Promise<WorkStepRead> => {
      return this.request({
        method: "PUT",
        url: `/work-steps/${stepId}`,
        data: body,
      })
    },

    delete: async (stepId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/work-steps/${stepId}`,
      })
    },

    /** PUT /work-steps/reorder — reassigns ag_pos_no in 10-steps.
     *  Items with agPosNo==0 must NOT be included in itemIds. */
    reorder: async (itemIds: string[]): Promise<WorkStepRead[]> => {
      return this.request({
        method: "PUT",
        url: "/work-steps/reorder",
        data: { itemIds },
      })
    },

    /** PUT /work-steps/{stepId}/move — move to a different position (LEH-102).
     *  targetPositionId=null moves to FAUF header (Allgemein). */
    move: async (stepId: string, body: MoveItemRequest): Promise<WorkStepRead> => {
      return this.request({
        method: "PUT",
        url: `/work-steps/${stepId}/move`,
        data: body,
      })
    },
  }

  // ===========================================================================
  // Reports / Dashboard (LEH-073)
  // ===========================================================================

  reports = {
    ordersOverview: async (): Promise<OrdersOverview> => {
      return this.request({
        method: "GET",
        url: "/reports/orders-overview",
      })
    },

    planVsActual: async (params: { dateFrom: string; dateTo: string }): Promise<PlanVsActual> => {
      return this.request({
        method: "GET",
        url: "/reports/plan-vs-actual",
        params,
      })
    },

    machineUtilizationReport: async (params: {
      dateFrom: string
      dateTo: string
      costCenterId?: string
    }): Promise<MachineUtilizationRow[]> => {
      return this.request({
        method: "GET",
        url: "/reports/machine-utilization-report",
        params,
      })
    },

    procurementOverview: async (): Promise<ProcurementOverview> => {
      return this.request({
        method: "GET",
        url: "/reports/procurement-overview",
      })
    },

    stockOverview: async (): Promise<StockOverview> => {
      return this.request({
        method: "GET",
        url: "/reports/stock-overview",
      })
    },

    revenueOverview: async (): Promise<RevenueOverview> => {
      return this.request({
        method: "GET",
        url: "/reports/revenue-overview",
      })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — Machines (LEH-090)
  // ===========================================================================

  machines = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<MachineListItem>> => {
      return this.request({ method: "GET", url: "/machines", params })
    },

    get: async (machineId: string): Promise<MachineRead> => {
      return this.request({ method: "GET", url: `/machines/${machineId}` })
    },

    create: async (body: MachineCreate): Promise<MachineRead> => {
      return this.request({ method: "POST", url: "/machines", data: body })
    },

    update: async (machineId: string, body: MachineUpdate): Promise<MachineRead> => {
      return this.request({ method: "PUT", url: `/machines/${machineId}`, data: body })
    },

    delete: async (machineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/machines/${machineId}` })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — CostCenterGroups (LEH-090)
  // ===========================================================================

  costCenterGroups = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<CostCenterGroupListItem>> => {
      return this.request({ method: "GET", url: "/cost-center-groups", params })
    },

    get: async (groupId: string): Promise<CostCenterGroupRead> => {
      return this.request({ method: "GET", url: `/cost-center-groups/${groupId}` })
    },

    create: async (body: CostCenterGroupCreate): Promise<CostCenterGroupRead> => {
      return this.request({ method: "POST", url: "/cost-center-groups", data: body })
    },

    update: async (groupId: string, body: CostCenterGroupUpdate): Promise<CostCenterGroupRead> => {
      return this.request({ method: "PUT", url: `/cost-center-groups/${groupId}`, data: body })
    },

    delete: async (groupId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/cost-center-groups/${groupId}` })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — CostCenters (LEH-090)
  // ===========================================================================

  costCenters = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      groupId?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<CostCenterListItem>> => {
      return this.request({ method: "GET", url: "/cost-centers", params })
    },

    get: async (costCenterId: string): Promise<CostCenterRead> => {
      return this.request({ method: "GET", url: `/cost-centers/${costCenterId}` })
    },

    create: async (body: CostCenterCreate): Promise<CostCenterRead> => {
      return this.request({ method: "POST", url: "/cost-centers", data: body })
    },

    update: async (costCenterId: string, body: CostCenterUpdate): Promise<CostCenterRead> => {
      return this.request({ method: "PUT", url: `/cost-centers/${costCenterId}`, data: body })
    },

    delete: async (costCenterId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/cost-centers/${costCenterId}` })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — OperationTypes (LEH-090)
  // ===========================================================================

  operationTypes = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<OperationTypeListItem>> => {
      return this.request({ method: "GET", url: "/operation-types", params })
    },

    get: async (operationTypeId: string): Promise<OperationTypeRead> => {
      return this.request({ method: "GET", url: `/operation-types/${operationTypeId}` })
    },

    create: async (body: OperationTypeCreate): Promise<OperationTypeRead> => {
      return this.request({ method: "POST", url: "/operation-types", data: body })
    },

    update: async (operationTypeId: string, body: OperationTypeUpdate): Promise<OperationTypeRead> => {
      return this.request({ method: "PUT", url: `/operation-types/${operationTypeId}`, data: body })
    },

    delete: async (operationTypeId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/operation-types/${operationTypeId}` })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — HourlyRates (LEH-090)
  // ===========================================================================

  hourlyRates = {
    list: async (params?: {
      page?: number
      pageSize?: number
      costCenterId?: string
    }): Promise<PaginatedResponse<HourlyRateListItem>> => {
      return this.request({ method: "GET", url: "/hourly-rates", params })
    },

    get: async (rateId: string): Promise<HourlyRateRead> => {
      return this.request({ method: "GET", url: `/hourly-rates/${rateId}` })
    },

    create: async (body: HourlyRateCreate): Promise<HourlyRateRead> => {
      return this.request({ method: "POST", url: "/hourly-rates", data: body })
    },

    update: async (rateId: string, body: HourlyRateUpdate): Promise<HourlyRateRead> => {
      return this.request({ method: "PUT", url: `/hourly-rates/${rateId}`, data: body })
    },

    delete: async (rateId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hourly-rates/${rateId}` })
    },

    activate: async (rateId: string): Promise<HourlyRateActivateResponse> => {
      return this.request({ method: "POST", url: `/hourly-rates/${rateId}/activate` })
    },
  }

  // ===========================================================================
  // Stammdaten Fertigung — CostObjects (LEH-090)
  // ===========================================================================

  costObjects = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<CostObjectListItem>> => {
      return this.request({ method: "GET", url: "/cost-objects", params })
    },

    get: async (costObjectId: string): Promise<CostObjectRead> => {
      return this.request({ method: "GET", url: `/cost-objects/${costObjectId}` })
    },

    create: async (body: CostObjectCreate): Promise<CostObjectRead> => {
      return this.request({ method: "POST", url: "/cost-objects", data: body })
    },

    update: async (costObjectId: string, body: CostObjectUpdate): Promise<CostObjectRead> => {
      return this.request({ method: "PUT", url: `/cost-objects/${costObjectId}`, data: body })
    },

    delete: async (costObjectId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/cost-objects/${costObjectId}` })
    },
  }

  // ===========================================================================
  // HR — Employees (LEH-092)
  // ===========================================================================

  hrEmployees = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
      costCenterId?: string
    }): Promise<PaginatedResponse<HrEmployeeListItem>> => {
      return this.request({ method: "GET", url: "/hr-employees", params })
    },

    get: async (employeeId: string): Promise<HrEmployeeRead> => {
      return this.request({ method: "GET", url: `/hr-employees/${employeeId}` })
    },

    create: async (body: HrEmployeeCreate): Promise<HrEmployeeRead> => {
      return this.request({ method: "POST", url: "/hr-employees", data: body })
    },

    update: async (employeeId: string, body: HrEmployeeUpdate): Promise<HrEmployeeRead> => {
      return this.request({ method: "PUT", url: `/hr-employees/${employeeId}`, data: body })
    },

    delete: async (employeeId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr-employees/${employeeId}` })
    },
  }

  // ===========================================================================
  // HR — Contracts (LEH-092)
  // ===========================================================================

  hrContracts = {
    list: async (
      employeeId: string,
      params?: { page?: number; pageSize?: number }
    ): Promise<PaginatedResponse<HrEmployeeContractListItem>> => {
      return this.request({
        method: "GET",
        url: `/hr-employees/${employeeId}/contracts`,
        params,
      })
    },

    get: async (employeeId: string, contractId: string): Promise<HrEmployeeContractRead> => {
      return this.request({
        method: "GET",
        url: `/hr-employees/${employeeId}/contracts/${contractId}`,
      })
    },

    create: async (employeeId: string, body: HrEmployeeContractCreate): Promise<HrEmployeeContractRead> => {
      return this.request({
        method: "POST",
        url: `/hr-employees/${employeeId}/contracts`,
        data: body,
      })
    },

    update: async (
      employeeId: string,
      contractId: string,
      body: HrEmployeeContractUpdate
    ): Promise<HrEmployeeContractRead> => {
      return this.request({
        method: "PUT",
        url: `/hr-employees/${employeeId}/contracts/${contractId}`,
        data: body,
      })
    },

    activate: async (employeeId: string, contractId: string): Promise<HrEmployeeContractRead> => {
      return this.request({
        method: "POST",
        url: `/hr-employees/${employeeId}/contracts/${contractId}/activate`,
      })
    },

    delete: async (employeeId: string, contractId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/hr-employees/${employeeId}/contracts/${contractId}`,
      })
    },
  }

  // ===========================================================================
  // HR — Work-Time Models (LEH-092)
  // ===========================================================================

  hrWorkTimeModels = {
    list: async (params?: {
      page?: number
      pageSize?: number
      isActive?: boolean
    }): Promise<PaginatedResponse<HrWorkTimeModelListItem>> => {
      return this.request({ method: "GET", url: "/hr-work-time-models", params })
    },

    get: async (modelId: string): Promise<HrWorkTimeModelRead> => {
      return this.request({ method: "GET", url: `/hr-work-time-models/${modelId}` })
    },

    create: async (body: HrWorkTimeModelCreate): Promise<HrWorkTimeModelRead> => {
      return this.request({ method: "POST", url: "/hr-work-time-models", data: body })
    },

    update: async (modelId: string, body: HrWorkTimeModelUpdate): Promise<HrWorkTimeModelRead> => {
      return this.request({ method: "PUT", url: `/hr-work-time-models/${modelId}`, data: body })
    },

    delete: async (modelId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/hr-work-time-models/${modelId}` })
    },
  }

  // ===========================================================================
  // HR — Leave (Urlaub) (LEH-092)
  // ===========================================================================

  hrLeave = {
    listAccounts: async (
      employeeId: string,
      params?: { page?: number; pageSize?: number }
    ): Promise<PaginatedResponse<HrLeaveAccountRead>> => {
      return this.request({
        method: "GET",
        url: `/hr-employees/${employeeId}/leave-accounts`,
        params,
      })
    },

    listRequests: async (
      employeeId: string,
      params?: { page?: number; pageSize?: number; status?: number }
    ): Promise<PaginatedResponse<HrLeaveRequestRead>> => {
      return this.request({
        method: "GET",
        url: `/hr-employees/${employeeId}/leave-requests`,
        params,
      })
    },

    createRequest: async (
      employeeId: string,
      body: HrLeaveRequestCreate
    ): Promise<HrLeaveRequestWithWarnings> => {
      return this.request({
        method: "POST",
        url: `/hr-employees/${employeeId}/leave-requests`,
        data: body,
      })
    },

    approve: async (
      employeeId: string,
      requestId: string,
      body: HrLeaveRequestApprove
    ): Promise<HrLeaveRequestWithWarnings> => {
      return this.request({
        method: "POST",
        url: `/hr-employees/${employeeId}/leave-requests/${requestId}/approve`,
        data: body,
      })
    },

    reject: async (
      employeeId: string,
      requestId: string,
      body: HrLeaveRequestReject
    ): Promise<HrLeaveRequestRead> => {
      return this.request({
        method: "POST",
        url: `/hr-employees/${employeeId}/leave-requests/${requestId}/reject`,
        data: body,
      })
    },

    deleteRequest: async (employeeId: string, requestId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/hr-employees/${employeeId}/leave-requests/${requestId}`,
      })
    },
  }

  // ===========================================================================
  // HR — Calendars (Betriebskalender) (LEH-092)
  // ===========================================================================

  hrCalendars = {
    list: async (params?: {
      page?: number
      pageSize?: number
    }): Promise<PaginatedResponse<HrCompanyCalendarRead>> => {
      return this.request({ method: "GET", url: "/hr-calendars", params })
    },

    get: async (calendarId: string): Promise<HrCompanyCalendarRead> => {
      return this.request({ method: "GET", url: `/hr-calendars/${calendarId}` })
    },

    generate: async (body: HrCalendarGenerateRequest): Promise<HrCompanyCalendarRead> => {
      return this.request({ method: "POST", url: "/hr-calendars/generate", data: body })
    },

    listHolidays: async (
      calendarId: string,
      params?: { page?: number; pageSize?: number }
    ): Promise<PaginatedResponse<HrHolidayRead>> => {
      return this.request({
        method: "GET",
        url: `/hr-calendars/${calendarId}/holidays`,
        params,
      })
    },
  }

  // ===========================================================================
  // Beschaffung — RFQs (LEH-093)
  // ===========================================================================

  rfqs = {
    list: async (params?: {
      page?: number
      pageSize?: number
      supplierId?: string
      status?: number
    }): Promise<PaginatedResponse<RfqListItem>> => {
      return this.request({ method: "GET", url: "/rfqs", params })
    },

    get: async (rfqId: string): Promise<RfqRead> => {
      return this.request({ method: "GET", url: `/rfqs/${rfqId}` })
    },

    create: async (body: RfqCreate): Promise<RfqRead> => {
      return this.request({ method: "POST", url: "/rfqs", data: body })
    },

    update: async (rfqId: string, body: RfqUpdate): Promise<RfqRead> => {
      return this.request({ method: "PUT", url: `/rfqs/${rfqId}`, data: body })
    },

    delete: async (rfqId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rfqs/${rfqId}` })
    },

    addLine: async (rfqId: string, body: RfqLineCreate): Promise<RfqLineRead> => {
      return this.request({ method: "POST", url: `/rfqs/${rfqId}/lines`, data: body })
    },

    updateLine: async (rfqId: string, lineId: string, body: RfqLineUpdate): Promise<RfqLineRead> => {
      return this.request({ method: "PUT", url: `/rfqs/${rfqId}/lines/${lineId}`, data: body })
    },

    deleteLine: async (rfqId: string, lineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/rfqs/${rfqId}/lines/${lineId}` })
    },

    convertToQuote: async (rfqId: string, body: RfqToQuoteRequest): Promise<SupplierQuoteRead> => {
      return this.request({ method: "POST", url: `/rfqs/${rfqId}/convert-to-quote`, data: body })
    },
  }

  // ===========================================================================
  // Beschaffung — SupplierQuotes (LEH-093)
  // ===========================================================================

  supplierQuotes = {
    list: async (params?: {
      page?: number
      pageSize?: number
      supplierId?: string
      rfqId?: string
    }): Promise<PaginatedResponse<SupplierQuoteListItem>> => {
      return this.request({ method: "GET", url: "/supplier-quotes", params })
    },

    get: async (quoteId: string): Promise<SupplierQuoteRead> => {
      return this.request({ method: "GET", url: `/supplier-quotes/${quoteId}` })
    },

    create: async (body: SupplierQuoteCreate): Promise<SupplierQuoteRead> => {
      return this.request({ method: "POST", url: "/supplier-quotes", data: body })
    },

    update: async (quoteId: string, body: SupplierQuoteUpdate): Promise<SupplierQuoteRead> => {
      return this.request({ method: "PUT", url: `/supplier-quotes/${quoteId}`, data: body })
    },

    delete: async (quoteId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/supplier-quotes/${quoteId}` })
    },

    addLine: async (quoteId: string, body: QuoteLineCreate): Promise<QuoteLineRead> => {
      return this.request({ method: "POST", url: `/supplier-quotes/${quoteId}/lines`, data: body })
    },

    updateLine: async (quoteId: string, lineId: string, body: QuoteLineUpdate): Promise<QuoteLineRead> => {
      return this.request({ method: "PUT", url: `/supplier-quotes/${quoteId}/lines/${lineId}`, data: body })
    },

    deleteLine: async (quoteId: string, lineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/supplier-quotes/${quoteId}/lines/${lineId}` })
    },

    convertToPo: async (quoteId: string, body: QuoteToPoRequest): Promise<PurchaseOrderRead> => {
      return this.request({ method: "POST", url: `/supplier-quotes/${quoteId}/convert-to-po`, data: body })
    },
  }

  // ===========================================================================
  // Beschaffung — PurchaseOrders (LEH-093)
  // ===========================================================================

  purchaseOrders = {
    list: async (params?: {
      page?: number
      pageSize?: number
      supplierId?: string
      status?: number
    }): Promise<PaginatedResponse<PurchaseOrderListItem>> => {
      return this.request({ method: "GET", url: "/purchase-orders", params })
    },

    get: async (poId: string): Promise<PurchaseOrderRead> => {
      return this.request({ method: "GET", url: `/purchase-orders/${poId}` })
    },

    create: async (body: PurchaseOrderCreate): Promise<PurchaseOrderRead> => {
      return this.request({ method: "POST", url: "/purchase-orders", data: body })
    },

    update: async (poId: string, body: PurchaseOrderUpdate): Promise<PurchaseOrderRead> => {
      return this.request({ method: "PUT", url: `/purchase-orders/${poId}`, data: body })
    },

    delete: async (poId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/purchase-orders/${poId}` })
    },

    addLine: async (poId: string, body: PoLineCreate): Promise<PoLineRead> => {
      return this.request({ method: "POST", url: `/purchase-orders/${poId}/lines`, data: body })
    },

    updateLine: async (poId: string, lineId: string, body: PoLineUpdate): Promise<PoLineRead> => {
      return this.request({ method: "PUT", url: `/purchase-orders/${poId}/lines/${lineId}`, data: body })
    },

    deleteLine: async (poId: string, lineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/purchase-orders/${poId}/lines/${lineId}` })
    },

    getOpenQuantities: async (poId: string): Promise<QuantityOpenResponse[]> => {
      return this.request({ method: "GET", url: `/purchase-orders/${poId}/open-quantities` })
    },

    createGoodsReceipt: async (poId: string, body: PoToGrRequest): Promise<GoodsReceiptRead> => {
      return this.request({ method: "POST", url: `/purchase-orders/${poId}/create-goods-receipt`, data: body })
    },
  }

  // ===========================================================================
  // Beschaffung — GoodsReceipts (LEH-093)
  // ===========================================================================

  goodsReceipts = {
    list: async (params?: {
      page?: number
      pageSize?: number
      poId?: string
    }): Promise<PaginatedResponse<GoodsReceiptListItem>> => {
      return this.request({ method: "GET", url: "/goods-receipts", params })
    },

    get: async (grId: string): Promise<GoodsReceiptRead> => {
      return this.request({ method: "GET", url: `/goods-receipts/${grId}` })
    },

    create: async (body: GoodsReceiptCreate): Promise<GoodsReceiptRead> => {
      return this.request({ method: "POST", url: "/goods-receipts", data: body })
    },

    update: async (grId: string, body: GoodsReceiptUpdate): Promise<GoodsReceiptRead> => {
      return this.request({ method: "PUT", url: `/goods-receipts/${grId}`, data: body })
    },

    delete: async (grId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/goods-receipts/${grId}` })
    },

    addLine: async (grId: string, body: GrLineCreate): Promise<GrLineRead> => {
      return this.request({ method: "POST", url: `/goods-receipts/${grId}/lines`, data: body })
    },

    updateLine: async (grId: string, lineId: string, body: GrLineUpdate): Promise<GrLineRead> => {
      return this.request({ method: "PUT", url: `/goods-receipts/${grId}/lines/${lineId}`, data: body })
    },

    deleteLine: async (grId: string, lineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/goods-receipts/${grId}/lines/${lineId}` })
    },

    postToStock: async (grId: string, body: PostToStockRequest): Promise<StockMovementRead[]> => {
      return this.request({ method: "POST", url: `/goods-receipts/${grId}/post-to-stock`, data: body })
    },
  }

  // ===========================================================================
  // Lager — Warehouses + Stock (LEH-093)
  // ===========================================================================

  warehouses = {
    list: async (params?: {
      page?: number
      pageSize?: number
      isActive?: boolean
    }): Promise<PaginatedResponse<WarehouseListItem>> => {
      return this.request({ method: "GET", url: "/warehouses", params })
    },

    get: async (warehouseId: string): Promise<WarehouseRead> => {
      return this.request({ method: "GET", url: `/warehouses/${warehouseId}` })
    },

    create: async (body: WarehouseCreate): Promise<WarehouseRead> => {
      return this.request({ method: "POST", url: "/warehouses", data: body })
    },

    update: async (warehouseId: string, body: WarehouseUpdate): Promise<WarehouseRead> => {
      return this.request({ method: "PUT", url: `/warehouses/${warehouseId}`, data: body })
    },

    delete: async (warehouseId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/warehouses/${warehouseId}` })
    },
  }

  stock = {
    listLevels: async (params?: {
      page?: number
      pageSize?: number
      warehouseId?: string
      articleId?: string
    }): Promise<PaginatedResponse<StockLevelRead>> => {
      return this.request({ method: "GET", url: "/stock/levels", params })
    },

    listMovements: async (params?: {
      page?: number
      pageSize?: number
      warehouseId?: string
      articleId?: string
      movementType?: number
    }): Promise<PaginatedResponse<StockMovementRead>> => {
      return this.request({ method: "GET", url: "/stock/movements", params })
    },
  }

  // ===========================================================================
  // Einkaufspreise — SupplierPrices (LEH-093)
  // ===========================================================================

  supplierPrices = {
    list: async (params?: {
      page?: number
      pageSize?: number
      articleId?: string
      supplierId?: string
      isActive?: boolean
    }): Promise<PaginatedResponse<SupplierPriceListItem>> => {
      return this.request({ method: "GET", url: "/supplier-prices", params })
    },

    get: async (priceId: string): Promise<SupplierPriceRead> => {
      return this.request({ method: "GET", url: `/supplier-prices/${priceId}` })
    },

    create: async (body: SupplierPriceCreate): Promise<SupplierPriceRead> => {
      return this.request({ method: "POST", url: "/supplier-prices", data: body })
    },

    update: async (priceId: string, body: SupplierPriceUpdate): Promise<SupplierPriceRead> => {
      return this.request({ method: "PUT", url: `/supplier-prices/${priceId}`, data: body })
    },

    delete: async (priceId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/supplier-prices/${priceId}` })
    },

    activate: async (priceId: string): Promise<SupplierPriceRead> => {
      return this.request({ method: "POST", url: `/supplier-prices/${priceId}/activate` })
    },

    addTier: async (priceId: string, body: SupplierPriceTierCreate): Promise<SupplierPriceTierRead> => {
      return this.request({ method: "POST", url: `/supplier-prices/${priceId}/tiers`, data: body })
    },

    updateTier: async (priceId: string, tierId: string, body: SupplierPriceTierUpdate): Promise<SupplierPriceTierRead> => {
      return this.request({ method: "PUT", url: `/supplier-prices/${priceId}/tiers/${tierId}`, data: body })
    },

    deleteTier: async (priceId: string, tierId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/supplier-prices/${priceId}/tiers/${tierId}` })
    },
  }

  // ===========================================================================
  // Eingangsrechnungen — PurchaseInvoices (LEH-093)
  // ===========================================================================

  purchaseInvoices = {
    list: async (params?: {
      page?: number
      pageSize?: number
      supplierId?: string
      status?: number
      matchStatus?: number
    }): Promise<PaginatedResponse<PurchaseInvoiceListItem>> => {
      return this.request({ method: "GET", url: "/purchase-invoices", params })
    },

    get: async (invoiceId: string): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "GET", url: `/purchase-invoices/${invoiceId}` })
    },

    create: async (body: PurchaseInvoiceCreate): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "POST", url: "/purchase-invoices", data: body })
    },

    update: async (invoiceId: string, body: PurchaseInvoiceUpdate): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "PUT", url: `/purchase-invoices/${invoiceId}`, data: body })
    },

    delete: async (invoiceId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/purchase-invoices/${invoiceId}` })
    },

    addLine: async (invoiceId: string, body: PurchaseInvoiceLineCreate): Promise<PurchaseInvoiceLineRead> => {
      return this.request({ method: "POST", url: `/purchase-invoices/${invoiceId}/lines`, data: body })
    },

    updateLine: async (invoiceId: string, lineId: string, body: PurchaseInvoiceLineUpdate): Promise<PurchaseInvoiceLineRead> => {
      return this.request({ method: "PUT", url: `/purchase-invoices/${invoiceId}/lines/${lineId}`, data: body })
    },

    deleteLine: async (invoiceId: string, lineId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/purchase-invoices/${invoiceId}/lines/${lineId}` })
    },

    runMatch: async (invoiceId: string, body: MatchRequest): Promise<MatchResult> => {
      return this.request({ method: "POST", url: `/purchase-invoices/${invoiceId}/match`, data: body })
    },

    getMatchResult: async (invoiceId: string): Promise<MatchResult> => {
      return this.request({ method: "GET", url: `/purchase-invoices/${invoiceId}/match-result` })
    },

    approve: async (invoiceId: string, note?: string): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "POST", url: `/purchase-invoices/${invoiceId}/approve`, data: { note } })
    },

    reject: async (invoiceId: string, note?: string): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "POST", url: `/purchase-invoices/${invoiceId}/reject`, data: { note } })
    },

    pay: async (invoiceId: string, note?: string): Promise<PurchaseInvoiceRead> => {
      return this.request({ method: "POST", url: `/purchase-invoices/${invoiceId}/pay`, data: { note } })
    },
  }

  // ===========================================================================
  // Erw. Fertigung — Tools (LEH-013 / LEH-094)
  // ===========================================================================

  tools = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
      machineId?: string
    }): Promise<PaginatedResponse<ToolListItem>> => {
      return this.request({ method: "GET", url: "/tools", params })
    },

    get: async (toolId: string): Promise<ToolRead> => {
      return this.request({ method: "GET", url: `/tools/${toolId}` })
    },

    create: async (body: ToolCreate): Promise<ToolRead> => {
      return this.request({ method: "POST", url: "/tools", data: body })
    },

    update: async (toolId: string, body: ToolUpdate): Promise<ToolRead> => {
      return this.request({ method: "PUT", url: `/tools/${toolId}`, data: body })
    },

    delete: async (toolId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/tools/${toolId}` })
    },
  }

  // ===========================================================================
  // Erw. Fertigung — NC-Programme (LEH-013 / LEH-094)
  // ===========================================================================

  ncPrograms = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
      machineId?: string
      articleId?: string
    }): Promise<PaginatedResponse<NcProgramListItem>> => {
      return this.request({ method: "GET", url: "/nc-programs", params })
    },

    get: async (programId: string): Promise<NcProgramRead> => {
      return this.request({ method: "GET", url: `/nc-programs/${programId}` })
    },

    create: async (body: NcProgramCreate): Promise<NcProgramRead> => {
      return this.request({ method: "POST", url: "/nc-programs", data: body })
    },

    update: async (programId: string, body: NcProgramUpdate): Promise<NcProgramRead> => {
      return this.request({ method: "PUT", url: `/nc-programs/${programId}`, data: body })
    },

    delete: async (programId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/nc-programs/${programId}` })
    },
  }

  // ===========================================================================
  // Erw. Fertigung — Prüfmittel / InspectionEquipment (LEH-013 / LEH-094)
  // ===========================================================================

  inspectionEquipment = {
    list: async (params?: {
      page?: number
      pageSize?: number
      search?: string
      isActive?: boolean
      calibrationStatus?: CalibrationStatus
    }): Promise<PaginatedResponse<InspectionEquipmentListItem>> => {
      return this.request({ method: "GET", url: "/inspection-equipment", params })
    },

    get: async (equipmentId: string): Promise<InspectionEquipmentRead> => {
      return this.request({ method: "GET", url: `/inspection-equipment/${equipmentId}` })
    },

    create: async (body: InspectionEquipmentCreate): Promise<InspectionEquipmentRead> => {
      return this.request({ method: "POST", url: "/inspection-equipment", data: body })
    },

    update: async (equipmentId: string, body: InspectionEquipmentUpdate): Promise<InspectionEquipmentRead> => {
      return this.request({ method: "PUT", url: `/inspection-equipment/${equipmentId}`, data: body })
    },

    delete: async (equipmentId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/inspection-equipment/${equipmentId}` })
    },
  }

  // ===========================================================================
  // Wartung — MaintenancePlans + Entries (LEH-081 / LEH-094)
  // ===========================================================================

  maintenancePlans = {
    listForMachine: async (
      machineId: string,
      params?: { page?: number; pageSize?: number; isActive?: boolean }
    ): Promise<PaginatedResponse<MaintenancePlanListItem>> => {
      return this.request({
        method: "GET",
        url: `/machines/${machineId}/maintenance-plans`,
        params,
      })
    },

    get: async (machineId: string, planId: string): Promise<MaintenancePlanRead> => {
      return this.request({
        method: "GET",
        url: `/machines/${machineId}/maintenance-plans/${planId}`,
      })
    },

    create: async (machineId: string, body: MaintenancePlanCreate): Promise<MaintenancePlanRead> => {
      return this.request({
        method: "POST",
        url: `/machines/${machineId}/maintenance-plans`,
        data: body,
      })
    },

    update: async (
      machineId: string,
      planId: string,
      body: MaintenancePlanUpdate
    ): Promise<MaintenancePlanRead> => {
      return this.request({
        method: "PUT",
        url: `/machines/${machineId}/maintenance-plans/${planId}`,
        data: body,
      })
    },

    delete: async (machineId: string, planId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/machines/${machineId}/maintenance-plans/${planId}`,
      })
    },

    due: async (params?: {
      asOf?: string
      page?: number
      pageSize?: number
    }): Promise<PaginatedResponse<DuePlanItem>> => {
      return this.request({ method: "GET", url: "/maintenance/due", params })
    },
  }

  maintenanceEntries = {
    listForMachine: async (
      machineId: string,
      params?: { page?: number; pageSize?: number; planId?: string }
    ): Promise<PaginatedResponse<MaintenanceEntryListItem>> => {
      return this.request({
        method: "GET",
        url: `/machines/${machineId}/maintenance-entries`,
        params,
      })
    },

    get: async (machineId: string, entryId: string): Promise<MaintenanceEntryRead> => {
      return this.request({
        method: "GET",
        url: `/machines/${machineId}/maintenance-entries/${entryId}`,
      })
    },

    record: async (machineId: string, body: MaintenanceEntryCreate): Promise<MaintenanceEntryRead> => {
      return this.request({
        method: "POST",
        url: `/machines/${machineId}/maintenance-entries`,
        data: body,
      })
    },

    update: async (
      machineId: string,
      entryId: string,
      body: MaintenanceEntryUpdate
    ): Promise<MaintenanceEntryRead> => {
      return this.request({
        method: "PUT",
        url: `/machines/${machineId}/maintenance-entries/${entryId}`,
        data: body,
      })
    },

    delete: async (machineId: string, entryId: string): Promise<void> => {
      return this.request({
        method: "DELETE",
        url: `/machines/${machineId}/maintenance-entries/${entryId}`,
      })
    },

    summary: async (machineId: string): Promise<MachineMaintenanceSummary> => {
      return this.request({
        method: "GET",
        url: `/machines/${machineId}/maintenance-entries/summary`,
      })
    },
  }

  // ===========================================================================
  // Mängelkatalog — DefectCatalog (LEH-052 / LEH-094)
  // ===========================================================================

  defectCatalog = {
    list: async (params?: {
      page?: number
      pageSize?: number
      isActive?: boolean
      category?: number
    }): Promise<PaginatedResponse<DefectCatalogRead>> => {
      return this.request({ method: "GET", url: "/defect-catalog", params })
    },

    get: async (defectId: string): Promise<DefectCatalogRead> => {
      return this.request({ method: "GET", url: `/defect-catalog/${defectId}` })
    },

    create: async (body: DefectCatalogCreate): Promise<DefectCatalogRead> => {
      return this.request({ method: "POST", url: "/defect-catalog", data: body })
    },

    update: async (defectId: string, body: DefectCatalogUpdate): Promise<DefectCatalogRead> => {
      return this.request({ method: "PATCH", url: `/defect-catalog/${defectId}`, data: body })
    },

    delete: async (defectId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/defect-catalog/${defectId}` })
    },
  }

  // ===========================================================================
  // BDE-Terminals — Admin CRUD + Token-Rotation (LEH-050 / LEH-094)
  // ===========================================================================

  bdeTerminals = {
    list: async (params?: {
      page?: number
      pageSize?: number
    }): Promise<PaginatedResponse<BdeTerminalRead>> => {
      return this.request({ method: "GET", url: "/bde-terminals", params })
    },

    get: async (terminalId: string): Promise<BdeTerminalRead> => {
      return this.request({ method: "GET", url: `/bde-terminals/${terminalId}` })
    },

    create: async (body: BdeTerminalCreate): Promise<BdeTerminalWithToken> => {
      return this.request({ method: "POST", url: "/bde-terminals", data: body })
    },

    update: async (terminalId: string, body: BdeTerminalUpdate): Promise<BdeTerminalRead> => {
      return this.request({ method: "PUT", url: `/bde-terminals/${terminalId}`, data: body })
    },

    delete: async (terminalId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/bde-terminals/${terminalId}` })
    },

    rotateToken: async (terminalId: string): Promise<BdeTerminalWithToken> => {
      return this.request({ method: "POST", url: `/bde-terminals/${terminalId}/rotate-token` })
    },
  }

  // ===========================================================================
  // Dokumente — Documents (LEH-070 / LEH-094)
  // ===========================================================================

  documents = {
    connectivity: async (): Promise<ConnectivityResponse> => {
      return this.request({ method: "GET", url: "/documents/connectivity" })
    },

    list: async (params: {
      entityType: string
      entityId: string
      page?: number
      pageSize?: number
    }): Promise<PaginatedResponse<DocumentListItem>> => {
      return this.request({ method: "GET", url: "/documents", params })
    },

    get: async (documentId: string): Promise<DocumentRead> => {
      return this.request({ method: "GET", url: `/documents/${documentId}` })
    },

    upload: async (
      file: File,
      params: {
        entityType: string
        entityId: string
        category?: number
        tags?: string
      }
    ): Promise<DocumentRead> => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("entityType", params.entityType)
      formData.append("entityId", params.entityId)
      if (params.category !== undefined) formData.append("category", String(params.category))
      if (params.tags) formData.append("tags", params.tags)
      return this.request({
        method: "POST",
        url: "/documents/upload",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    update: async (documentId: string, body: DocumentUpdate): Promise<DocumentRead> => {
      return this.request({ method: "PUT", url: `/documents/${documentId}`, data: body })
    },

    delete: async (documentId: string): Promise<void> => {
      return this.request({ method: "DELETE", url: `/documents/${documentId}` })
    },

    downloadUrl: (documentId: string): string => {
      return `${API_BASE_URL}/documents/${documentId}/download`
    },

    // Authenticated download — a bare <a href> cannot carry the Bearer
    // token, so file bytes must come through the axios client (LEH-109).
    download: async (documentId: string): Promise<Blob> => {
      return this.request({
        method: "GET",
        url: `/documents/${documentId}/download`,
        responseType: "blob",
      })
    },
  }
}

export const apiClient = new ApiService()

// ===========================================================================
// Terminal API Client — X-Device-Token (no OIDC Bearer)
// LEH-051: /api/v1/bde/terminal/* routes
// ===========================================================================

const BDE_DEVICE_TOKEN_KEY = "bde_device_token"

/** Read device token from localStorage. Returns null if not set. */
export function getDeviceToken(): string | null {
  return localStorage.getItem(BDE_DEVICE_TOKEN_KEY)
}

/** Persist device token to localStorage. */
export function setDeviceToken(token: string): void {
  localStorage.setItem(BDE_DEVICE_TOKEN_KEY, token)
}

/** Remove device token from localStorage (terminal sign-out). */
export function clearDeviceToken(): void {
  localStorage.removeItem(BDE_DEVICE_TOKEN_KEY)
}

class TerminalApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    })

    // Attach X-Device-Token from localStorage on every request.
    // NEVER attaches an OIDC Bearer Token.
    this.client.interceptors.request.use((config) => {
      const token = getDeviceToken()
      if (token) {
        config.headers["X-Device-Token"] = token
      }
      return config
    })
  }

  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { detail?: unknown; message?: unknown } | undefined
        const detail = data?.detail
        const message =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail
                  .map((item: unknown) => {
                    if (typeof item === "object" && item !== null) {
                      const i = item as Record<string, unknown>
                      return typeof i.msg === "string" ? i.msg : JSON.stringify(item)
                    }
                    return String(item)
                  })
                  .join("; ")
              : typeof data?.message === "string"
                ? data.message
                : error.message
        const apiError = new Error(message) as Error & {
          response?: typeof error.response
          status?: number
        }
        apiError.response = error.response
        apiError.status = error.response?.status
        throw apiError
      }
      throw error
    }
  }

  /** POST /bde/terminal/rfid-login — resolve employee from RFID UID. */
  async rfidLogin(rfidUid: string): Promise<BdeRfidLoginResponse> {
    return this.request({
      method: "POST",
      url: "/bde/terminal/rfid-login",
      data: { rfidUid },
    })
  }

  /** GET /bde/terminal/current?employee_id=… — get running booking for worker. */
  async getCurrentBooking(employeeId: string): Promise<BdeCurrentBookingResponse> {
    return this.request({
      method: "GET",
      url: "/bde/terminal/current",
      params: { employee_id: employeeId },
    })
  }

  /** POST /bde/terminal/start — start a new BDE booking. */
  async startBooking(payload: BdeStartRequest): Promise<BdeStartResponse> {
    return this.request({
      method: "POST",
      url: "/bde/terminal/start",
      data: payload,
    })
  }

  /** POST /bde/terminal/stop — stop running booking with quantities. */
  async stopBooking(payload: BdeStopRequest): Promise<BdeStopResponse> {
    return this.request({
      method: "POST",
      url: "/bde/terminal/stop",
      data: payload,
    })
  }

  /** POST /bde/terminal/pause — add pause minutes to running booking. */
  async addPause(payload: BdePauseRequest): Promise<BdePauseResponse> {
    return this.request({
      method: "POST",
      url: "/bde/terminal/pause",
      data: payload,
    })
  }
}

export const terminalApiClient = new TerminalApiService()
